import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { NotFound } from "@/components/pages";
import { useOperator } from "@/hooks";
import { useGetVehicleQuery } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";

export interface IEditVehicleContext {
  vehicle: Vehicle;
}
export const EditVehicleContext = createContext<IEditVehicleContext | null>(null);

export const EditVehicleProvider = ({ children }: PropsWithChildren) => {
  const { id: operatorId } = useOperator();
  const { vehicleId } = useParams<{ vehicleId: string }>();

  const { data: vehicle, isFetching, isError } = useGetVehicleQuery(vehicleId ?? "", { skip: !vehicleId });

  const isOperatorOwnedVehicle = useMemo(() => {
    return vehicle?.owner.uuid === operatorId;
  }, [operatorId, vehicle?.owner.uuid]);

  if (isError || !isOperatorOwnedVehicle) return <NotFound noBackground noHeader />;
  if (isFetching || !vehicle) return <Loading />;

  return (
    <EditVehicleContext.Provider value={{ vehicle }}>
      {children}
    </EditVehicleContext.Provider>
  );
};

export const useEditVehicleContext = () => {
  const context = useContext(EditVehicleContext);
  if (!context) {
    throw new Error("useEditVehicleContext must be used within an EditVehicleProvider");
  }
  return context;
};
